import React, { useCallback, type FC } from 'react'

import { useDroppable, type UniqueIdentifier } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS, type Transform } from '@dnd-kit/utilities'
import styled from '@emotion/styled'

import colorNames from '../../design/color-names.js'
import {
  StyledPlaceholder,
  StyledStageItem,
  StyledStageItemContent,
  StyledStageItemControls,
  StyledStageItemExtraInfo,
  StyledStageList,
} from './FormBuilder.styles.ts'

import {
  ACTION_BUTTON_TYPE_DELETE,
  ButtonAction,
} from '../ButtonAction/ButtonAction.js'

const browserSvg = `<svg width="600" height="50" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g transform="translate(18 18)" stroke="#D6D9E2" stroke-width="2"><circle cx="7" cy="7" r="6"/><circle cx="31" cy="7" r="6"/><circle cx="55" cy="7" r="6"/></g><path d="M111 35V15H91.991a.99.99 0 00-.991.991V34.01a.99.99 0 00.991.991H111z" stroke="#D6D9E2" stroke-width="2"/><path d="M97.939 25a.794.794 0 01.23-.572l4.53-4.531a.796.796 0 011.13.003.803.803 0 01.002 1.128L99.859 25l3.972 3.972a.803.803 0 01-.003 1.128.796.796 0 01-1.128.003l-4.531-4.53a.794.794 0 01-.23-.573z" fill="#D6D9E2"/><path d="M111 15v20h19.009a.99.99 0 00.991-.991V15.99a.99.99 0 00-.991-.991H111z" stroke="#D6D9E2" stroke-width="2"/><path d="M124.061 25a.794.794 0 00-.23-.572l-4.53-4.531a.796.796 0 00-1.13.003.803.803 0 00-.002 1.128L122.141 25l-3.972 3.972a.803.803 0 00.003 1.128.796.796 0 001.128.003l4.531-4.53a.794.794 0 00.23-.573z" fill="#D6D9E2"/><rect stroke="#D6D9E2" stroke-width="2" x="161" y="15" width="416" height="20" rx="2"/></g></svg>`

const StyledContainer = styled.div`
  padding: 0px;
  margin-right: 32px;
  border: 2px solid ${colorNames.geyser};
  background-color: white;

  :before {
    position: relative;
    background-color: #f5f5f5;
    content: '';
    display: block;
    width: 100%;
    height: 52px;
    border-bottom: 2px solid ${colorNames.geyser};
    background-repeat: no-repeat;
    background-image: url('data:image/svg+xml;charset=utf8,${encodeURIComponent(
      browserSvg,
    )}');
  }
`

interface FormBuilderStageProps {
  className?: string
  'data-mu-test'?: string
  items: any[]
  selectedId?: UniqueIdentifier
  onEditItem: (id: UniqueIdentifier) => void
  onDeleteItem: (id: UniqueIdentifier) => void
  EmptyStateComponent: React.ComponentType
  globalConfig: any
  dragFromToolbox: boolean
  dropIndex?: number
}

interface ItemProps {
  id: UniqueIdentifier
  validate?: (c: any, cg: any) => boolean
  renderStageComponent: (c: any) => any
  renderStageExtraInfoComponent?: (c: any) => any
  config: any
}

interface SortableItemProps {
  item: ItemProps
  selectedId?: UniqueIdentifier
  globalConfig: any
  onEditItem: (id: UniqueIdentifier) => void
  onDeleteItem: (id: UniqueIdentifier) => void
}

const SortableItem: FC<SortableItemProps> = ({
  item,
  selectedId,
  globalConfig,
  onEditItem,
  onDeleteItem,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: {
      type: 'stage-item',
    },
  })

  const style = {
    transform: CSS.Transform.toString(transform as unknown as Transform | null),
    transition,
    scaleY: 1,
  }

  const handleClick = useCallback(() => {
    onEditItem(item.id)
  }, [item.id, onEditItem])

  const handleDelete = useCallback(() => {
    onDeleteItem(item.id)
  }, [item.id, onDeleteItem])

  return isDragging ? (
    <StyledPlaceholder
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
    />
  ) : (
    <StyledStageItem
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      selected={selectedId === item.id}
      invalid={
        typeof item.validate === 'function' &&
        !item.validate(item.config, globalConfig)
      }
      onClick={handleClick}
      data-mu-test='stage-item'
    >
      <StyledStageItemControls
        className='controls'
        selected={selectedId === item.id}
      >
        <ButtonAction
          type={ACTION_BUTTON_TYPE_DELETE}
          onClick={handleDelete}
          data-mu-test='action-delete'
        />
      </StyledStageItemControls>
      <StyledStageItemContent>
        {item.renderStageComponent(item.config)}
      </StyledStageItemContent>
      <StyledStageItemExtraInfo>
        {item.renderStageExtraInfoComponent?.({
          ...item.config,
          ...globalConfig,
        })}
      </StyledStageItemExtraInfo>
    </StyledStageItem>
  )
}

interface DropPlaceholderProps {
  position?: 'top' | 'bottom'
  index?: number
}

const DropPlaceholder: FC<DropPlaceholderProps> = ({ position, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    //  isDragging,
  } = useSortable({
    id: 'placeholder',
    data: {
      type: 'placeholder',
      position,
      index,
    },
  })

  const style = {
    transform: CSS.Transform.toString(transform as unknown as Transform | null),
    transition,
  }
  //
  return (
    <StyledPlaceholder
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
    />
  )
}

export const FormBuilderStage: FC<FormBuilderStageProps> = ({
  className,
  'data-mu-test': dataAttributeTest,
  items,
  selectedId,
  onEditItem,
  onDeleteItem,
  EmptyStateComponent,
  globalConfig,
  dragFromToolbox,
  dropIndex,
}) => {
  const { setNodeRef } = useDroppable({
    id: 'stage',
    data: {
      accepts: ['toolbox'],
      type: 'stage',
    },
  })

  return (
    <StyledContainer
      ref={setNodeRef}
      className={className}
      data-mu-test={dataAttributeTest}
      id='stage-form-builder'
    >
      <StyledStageList isDraggingOver={false}>
        {items.length === 0 && <EmptyStateComponent />}
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {items.map((o, index) => (
            <React.Fragment key={o.id}>
              {dragFromToolbox && dropIndex === index && (
                <DropPlaceholder index={index} />
              )}
              <SortableItem
                item={o}
                selectedId={selectedId}
                globalConfig={globalConfig}
                onEditItem={onEditItem}
                onDeleteItem={onDeleteItem}
              />
            </React.Fragment>
          ))}
          {dragFromToolbox && dropIndex === -1 && (
            <DropPlaceholder index={dropIndex} />
          )}
        </SortableContext>
      </StyledStageList>
    </StyledContainer>
  )
}
